"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { FallbackImage } from "@/components/common/FallbackImage";
import { useAuth } from "@/components/auth/Auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWater } from "@fortawesome/free-solid-svg-icons";
import { buildDirectMessageIdentityCandidates, normalizeDirectMessageIdentity } from "@/helpers/waves/direct-message-profile.helpers";
interface WavePictureProps {
  readonly name: string;
  readonly picture: string | null;
  readonly contributors: ReadonlyArray<{
    readonly pfp: string;
    readonly identity?: string | null;
  }>;
  readonly roundedClassName?: string | undefined;
}
const polygonsByCount: Record<number, string[]> = {
  // 1 entire area
  1: ["polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)"],
  // 2 halves: left, right
  2: ["polygon(0% 0%, 50% 0%, 50% 100%, 0% 100%)",
  // left
  "polygon(50% 0%, 100% 0%, 100% 100%, 50% 100%)" // right
  ],
  // 3 horizontal strips: top, middle, bottom
  3: ["polygon(0% 0%, 100% 0%, 100% 33.33%, 0% 33.33%)",
  // top
  "polygon(0% 33.33%, 100% 33.33%, 100% 66.66%, 0% 66.66%)",
  // middle
  "polygon(0% 66.66%, 100% 66.66%, 100% 100%, 0% 100%)" // bottom
  ],
  // 4 quadrants
  4: ["polygon(0% 0%, 50% 0%, 50% 50%, 0% 50%)",
  // top-left
  "polygon(50% 0%, 100% 0%, 100% 50%, 50% 50%)",
  // top-right
  "polygon(0% 50%, 50% 50%, 50% 100%, 0% 100%)",
  // bottom-left
  "polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)" // bottom-right
  ],
  // 5 horizontal strips (each 20% height)
  5: ["polygon(0% 0%, 100% 0%, 100% 20%, 0% 20%)", "polygon(0% 20%, 100% 20%, 100% 40%, 0% 40%)", "polygon(0% 40%, 100% 40%, 100% 60%, 0% 60%)", "polygon(0% 60%, 100% 60%, 100% 80%, 0% 80%)", "polygon(0% 80%, 100% 80%, 100% 100%, 0% 100%)"],
  // 6 => 2 rows × 3 columns each
  6: [
  // top-left
  "polygon(0% 0%, 33.33% 0%, 33.33% 50%, 0% 50%)",
  // top-middle
  "polygon(33.33% 0%, 66.66% 0%, 66.66% 50%, 33.33% 50%)",
  // top-right
  "polygon(66.66% 0%, 100% 0%, 100% 50%, 66.66% 50%)",
  // bottom-left
  "polygon(0% 50%, 33.33% 50%, 33.33% 100%, 0% 100%)",
  // bottom-middle
  "polygon(33.33% 50%, 66.66% 50%, 66.66% 100%, 33.33% 100%)",
  // bottom-right
  "polygon(66.66% 50%, 100% 50%, 100% 100%, 66.66% 100%)"]
};
export default function WavePicture(t0) {
  const $ = _c(25);
  const {
    name,
    picture,
    contributors,
    roundedClassName: t1
  } = t0;
  const roundedClassName = t1 === undefined ? "tw-rounded-full" : t1;
  const {
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const t2 = activeProfileProxy?.created_by;
  let t3;
  if ($[0] !== connectedProfile || $[1] !== t2) {
    t3 = buildDirectMessageIdentityCandidates(connectedProfile, t2);
    $[0] = connectedProfile;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const authenticatedIdentityCandidates = t3;
  if (picture) {
    const t4 = `tw-relative tw-h-full tw-w-full tw-overflow-hidden ${roundedClassName}`;
    let t5;
    if ($[3] !== name || $[4] !== picture) {
      t5 = <FallbackImage primarySrc={picture} fallbackSrc={picture} alt={name} fill={true} sizes="64px" className="tw-object-cover" />;
      $[3] = name;
      $[4] = picture;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t4 || $[7] !== t5) {
      t6 = <div className={t4}>{t5}</div>;
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  let t4;
  let t5;
  let t6;
  if ($[9] !== authenticatedIdentityCandidates || $[10] !== contributors || $[11] !== roundedClassName) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t7;
      if ($[15] !== authenticatedIdentityCandidates) {
        t7 = contributor => {
          if (!contributor.pfp) {
            return false;
          }
          const normalizedContributorIdentity = normalizeDirectMessageIdentity(contributor.identity);
          if (!normalizedContributorIdentity) {
            return true;
          }
          return !authenticatedIdentityCandidates.has(normalizedContributorIdentity);
        };
        $[15] = authenticatedIdentityCandidates;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      const pfps = contributors.filter(t7).map(_temp);
      if (pfps.length === 0) {
        const t8 = `tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-white/10 ${roundedClassName}`;
        let t9;
        if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <FontAwesomeIcon icon={faWater} className="tw-size-[54%] tw-text-white/60" aria-hidden="true" focusable={false} data-testid="wave-fallback-icon" />;
          $[17] = t9;
        } else {
          t9 = $[17];
        }
        let t10;
        if ($[18] !== t8) {
          t10 = <div className={t8}>{t9}</div>;
          $[18] = t8;
          $[19] = t10;
        } else {
          t10 = $[19];
        }
        t6 = t10;
        break bb0;
      }
      const sliceCount = Math.min(pfps.length, 6);
      const polygons = polygonsByCount[sliceCount];
      t4 = `tw-relative tw-h-full tw-w-full tw-overflow-hidden ${roundedClassName}`;
      let t8;
      if ($[20] !== polygons) {
        t8 = (pfp, i) => {
          const clip = polygons?.[i];
          return <div key={pfp + i} className="tw-absolute tw-inset-0" style={{
            clipPath: clip
          }}><FallbackImage primarySrc={pfp} fallbackSrc={pfp} alt={`Contributor-${i}`} fill={true} sizes="64px" className="tw-block tw-object-cover" /></div>;
        };
        $[20] = polygons;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t5 = pfps.slice(0, sliceCount).map(t8);
    }
    $[9] = authenticatedIdentityCandidates;
    $[10] = contributors;
    $[11] = roundedClassName;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[22] !== t4 || $[23] !== t5) {
    t7 = <div className={t4}>{t5}</div>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp(contributor_0) {
  return contributor_0.pfp;
}